const METERS_TO_MILES_RATIO = 0.00062137;
class NumberUtils {
  /**
   * @param amount
   * @returns Example: $2,350.50
   */
  static getCurrency(amount: number, maximumFractionDigits?: number): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: maximumFractionDigits !== undefined ? maximumFractionDigits : 2
    });
    return formatter.format(amount);
  }

  static percentageFormatter(value: number): string {
    return `${value}%`;
  }

  /**
   * @param filesize in bytes
   * @returns Example: 1 KB, 2.78 MB
   */
  static getFileSize(filesize: number): string {
    if (filesize === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(filesize) / Math.log(k));

    return parseFloat((filesize / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * @param meters
   * @returns meters equivalent in miles
   */
  static getMetersToMiles(meters: number): number {
    return meters * METERS_TO_MILES_RATIO;
  }

  /**
   * @param number to be rounded
   * @param decimals - number of decimals to be rounded to
   * @returns Example: 1.8288 -> 1.83
   */
  // TODO find where this is used for currency and change it to round currency properly
  static roundNumber(number: number, decimals = 2): number {
    const factor = Math.pow(10, decimals);
    // Adding a very small value to fix floating-point rounding issues
    // for example... 39.995 was incorrectly rounding to 39.99 instead of 40.
    // The addition of 1e-9 helps with numbers that are just below the boundary, like 39.995 being treated as 39.994999999.... The small addition pushes it over the edge, allowing the rounding to behave as expected.
    return Math.round(number * factor + 1e-9) / factor;
  }

  /**
   *
   * @param value string with format example $1,000
   * @returns number without format example 1000.00
   */
  static reverseCurrency(value: string): number {
    const exp = /^\w{0,3}\W?\s?(\d+)[.,](\d+)?,?(\d+)?$/g;
    const replacer = (f: unknown, group1: unknown, group2: unknown, group3: unknown): string => {
      return group3 ? `${group1}${group2}.${group3}` : `${group1}.${group2}`;
    };

    return Number(value.replace(exp, replacer));
  }

  /**
   * @param value
   * @returns Example: 1,232,000
   */
  static formatNumber(value: number, maximumFractionDigits = 5, minimumFractionDigits = 0): string {
    const formatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: maximumFractionDigits,
      minimumFractionDigits: minimumFractionDigits
    });
    return formatter.format(value);
  }

  static calculateCurrencyAmount(
    rate: number | null | undefined,
    quantity: number | null | undefined
  ): number {
    return Math.round((rate ?? 0) * 100 * (quantity ?? 0)) / 100;
  }

  /**
   * Calculates the currency amount with a specified precision.
   * @param rate - The rate to calculate the amount for.
   * @param quantity - The quantity to calculate the amount for.
   * @param precision - The precision to calculate the amount for.
   * @returns The calculated currency amount.
   */
  static calculateCurrencyAmountWithPrecision(
    rate: number | null | undefined,
    quantity: number | null | undefined,
    precision = 4
  ): number {
    const factor = Math.pow(10, precision);
    return Math.round((rate ?? 0) * factor * (quantity ?? 0)) / factor;
  }
}

export default NumberUtils;
